/* eslint-disable no-console */
import { useAlert } from 'dashboard/composables';
import i18n from 'dashboard/i18n/instance';
import NotificationSubscriptions from '../api/notificationSubscription';
import auth from '../api/auth';

export const verifyServiceWorkerExistence = (callback = () => {}) => {
  if (!('serviceWorker' in navigator)) {
    // Service Worker isn't supported on this browser, disable or hide UI.
    return;
  }

  if (!('PushManager' in window)) {
    // Push isn't supported on this browser, disable or hide UI.
    return;
  }

  navigator.serviceWorker
    .register('/sw.js')
    .then(registration => callback(registration))
    .catch(registrationError => {
      // eslint-disable-next-line
      console.log('SW registration failed: ', registrationError);
    });
};

export const hasPushPermissions = () => {
  if ('Notification' in window) {
    return Notification.permission === 'granted';
  }
  return false;
};

const generateKeys = str =>
  btoa(String.fromCharCode.apply(null, new Uint8Array(str)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_');

export const getPushSubscriptionPayload = subscription => ({
  subscription_type: 'browser_push',
  subscription_attributes: {
    endpoint: subscription.endpoint,
    p256dh: generateKeys(subscription.getKey('p256dh')),
    auth: generateKeys(subscription.getKey('auth')),
  },
});

export const sendRegistrationToServer = subscription => {
  if (auth.hasAuthCookie()) {
    return NotificationSubscriptions.create(
      getPushSubscriptionPayload(subscription)
    );
  }
  return null;
};

const pushSubscriptionOptions = () => ({
  userVisibleOnly: true,
  applicationServerKey: window.chatwootConfig.vapidPublicKey,
});

const subscribeToPush = async serviceWorkerRegistration => {
  const options = pushSubscriptionOptions();
  try {
    return await serviceWorkerRegistration.pushManager.subscribe(options);
  } catch (error) {
    // VAPID key rotated (or env/DB keys changed) while an old browser
    // subscription still exists — unsubscribe then create a fresh one.
    if (error?.name !== 'InvalidStateError') {
      throw error;
    }
    const existing =
      await serviceWorkerRegistration.pushManager.getSubscription();
    if (existing) {
      await existing.unsubscribe();
    }
    return serviceWorkerRegistration.pushManager.subscribe(options);
  }
};

export const registerSubscription = (onSuccess = () => {}) => {
  if (!window.chatwootConfig.vapidPublicKey) {
    return;
  }
  navigator.serviceWorker.ready
    .then(subscribeToPush)
    .then(sendRegistrationToServer)
    .then(() => {
      onSuccess();
    })
    .catch(error => {
      // eslint-disable-next-line no-console
      console.error('Push subscription registration failed:', error);
    });
};

export const requestPushPermissions = ({ onSuccess }) => {
  if (!('Notification' in window)) {
    // eslint-disable-next-line no-console
    console.warn('Notification is not supported');
    useAlert(
      i18n.global.t(
        'PROFILE_SETTINGS.FORM.PUSH_NOTIFICATIONS_SECTION.UNSUPPORTED_BROWSER'
      )
    );
  } else if (Notification.permission === 'granted') {
    registerSubscription(onSuccess);
  } else if (Notification.permission !== 'denied') {
    Notification.requestPermission(permission => {
      if (permission === 'granted') {
        registerSubscription(onSuccess);
      }
    });
  }
};
